import { useMemo, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { Loader2, Package, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { BomImportDropZone } from '@/shared/components/bom/BomImportDropZone'
import {
  useCreatePackagingShipment,
  useListPackagingShipmentStats,
  useListPackagingShipments,
  useParsePackagingShipmentSkuFile,
  useReviewPackagingShipmentSkus,
  type ShipmentSkuFileNeedsColumn,
  type ShipmentSkuFilePhase,
} from '@/shared/hooks/api/packagingShipments'
import { useSearchParamsNavigate } from '@/shared/hooks/use-search-params-navigate'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { MAX_UPLOAD_FILE_SIZE_BYTES } from '@/shared/lib/uploadGuardrails'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { PackagingSuppliersStatCardSkeleton } from './PackagingSuppliersStatCardSkeleton'
import { ShipmentSkuColumnMappingDialog } from './ShipmentSkuColumnMappingDialog'
import { ShipmentSkuMatchReview } from './ShipmentSkuMatchReview'
import {
  pastedShipmentSkus,
  SHIPMENT_PASTE_MAX_UNIQUE_SKUS,
  uniqueShipmentSkus,
  type ShipmentSkuReviewItem,
} from './ShipmentSkuReviewList'
import { ShipmentStatsRow } from './ShipmentStatsRow'
import { ShipmentsTable } from './ShipmentsTable'
import { skuFileErrorMessage } from './shipmentSkuFileError'
import {
  isAllowedShipmentSkuFile,
  SHIPMENT_SKU_FILE_ACCEPT,
} from './shipmentSkuFileTypes'

const SHIPMENT_STAT_CARD_COUNT = 4

function parseSortBy(value: unknown): 'name' | 'skusUpdatedAt' | 'createdAt' {
  if (value === 'name' || value === 'skusUpdatedAt' || value === 'createdAt') {
    return value
  }
  return 'createdAt'
}

function parseSortDir(value: unknown): 'asc' | 'desc' {
  return value === 'asc' ? 'asc' : 'desc'
}

function parsePositiveInt(value: unknown, fallback: number): number {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  if (Number.isNaN(parsed) || parsed < 1) {
    return fallback
  }
  return parsed
}

export function ShipmentsPage() {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const canWriteOrgData = useCanWriteOrgData()
  const { search, navigate: searchNavigate } = useSearchParamsNavigate()
  const sortBy = parseSortBy(search.sortBy)
  const sortDir = parseSortDir(search.sortDir)
  const listQuery = useMemo(
    () => ({
      page: parsePositiveInt(search.page, 1),
      pageSize: Math.min(parsePositiveInt(search.pageSize, 20), 50),
      sortBy,
      sortDir,
    }),
    [search.page, search.pageSize, sortBy, sortDir]
  )

  const { data, isLoading, isFetching, isError, refetch } =
    useListPackagingShipments(listQuery)
  const { data: stats, isLoading: isStatsLoading } =
    useListPackagingShipmentStats()
  const navigate = useNavigate()
  const createMutation = useCreatePackagingShipment()
  const parseSkuFile = useParsePackagingShipmentSkuFile()
  const reviewSkus = useReviewPackagingShipmentSkus()
  const uploadGenerationRef = useRef(0)

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [createStep, setCreateStep] = useState<'details' | 'review'>('details')
  const [showDropZone, setShowDropZone] = useState(true)
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [pasteText, setPasteText] = useState('')
  const [skuEntry, setSkuEntry] = useState<'upload' | 'paste'>('upload')
  const [extractedSkus, setExtractedSkus] = useState<string[] | null>(null)
  const [reviewItems, setReviewItems] = useState<
    ShipmentSkuReviewItem[] | null
  >(null)
  const [uploadFileError, setUploadFileError] = useState<string | null>(null)
  const [skuFilePhase, setSkuFilePhase] = useState<ShipmentSkuFilePhase | null>(
    null
  )
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [pendingFileKey, setPendingFileKey] = useState<string | null>(null)
  const [columnPrompt, setColumnPrompt] =
    useState<ShipmentSkuFileNeedsColumn | null>(null)

  const items = data?.items ?? []
  const pageInfo = data?.pageInfo
  const totalShipments = stats?.total ?? pageInfo?.totalCount ?? 0
  const showListError = isError && !isLoading
  const showEmptyState =
    !isStatsLoading && !isLoading && !showListError && totalShipments === 0
  const showStatsSkeleton = !showEmptyState && isStatsLoading
  const showStats = !showEmptyState && !isStatsLoading && stats != null

  const resetForm = () => {
    uploadGenerationRef.current += 1
    setName('')
    setNote('')
    setPasteText('')
    setSkuEntry('upload')
    setExtractedSkus(null)
    setReviewItems(null)
    setUploadFileError(null)
    setSkuFilePhase(null)
    setFormErrors([])
    setPendingFileKey(null)
    setColumnPrompt(null)
    setShowDropZone(true)
    setSelectedFileName(null)
    setCreateStep('details')
  }

  const applyParsedFile = (
    result: Awaited<ReturnType<typeof parseSkuFile.mutateAsync>>
  ) => {
    if (result.outcome === 'needsColumn') {
      setPendingFileKey(result.fileKey)
      setColumnPrompt(result)
      setExtractedSkus(null)
      setReviewItems(null)
      setShowDropZone(false)
      return
    }
    setColumnPrompt(null)
    setPendingFileKey(null)
    setExtractedSkus(uniqueShipmentSkus(result.skus))
    setReviewItems(result.items)
    setShowDropZone(false)
  }

  const clearParsedFile = () => {
    setExtractedSkus(null)
    setReviewItems(null)
    setColumnPrompt(null)
    setPendingFileKey(null)
    setSkuFilePhase(null)
    setShowDropZone(true)
  }

  const parseSelectedFile = async (file: File) => {
    const generation = uploadGenerationRef.current + 1
    uploadGenerationRef.current = generation
    setUploadFileError(null)
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      setUploadFileError(t('shipmentsPage.skuFile.tooLarge'))
      clearParsedFile()
      return
    }
    try {
      const result = await parseSkuFile.mutateAsync({
        file,
        onPhaseChange: (phase) => {
          if (uploadGenerationRef.current === generation) {
            setSkuFilePhase(phase)
          }
        },
      })
      if (uploadGenerationRef.current !== generation) {
        return
      }
      applyParsedFile(result)
    } catch (error) {
      if (uploadGenerationRef.current !== generation) {
        return
      }
      clearParsedFile()
      setUploadFileError(skuFileErrorMessage(error, t))
    } finally {
      if (uploadGenerationRef.current === generation) {
        setSkuFilePhase(null)
      }
    }
  }

  const parseChosenColumn = (skuColumn: number) => {
    if (pendingFileKey == null) {
      return
    }
    const generation = uploadGenerationRef.current + 1
    uploadGenerationRef.current = generation
    setUploadFileError(null)
    setSkuFilePhase('reading')
    void parseSkuFile
      .mutateAsync({ fileKey: pendingFileKey, skuColumn })
      .then((result) => {
        if (uploadGenerationRef.current !== generation) {
          return
        }
        applyParsedFile(result)
      })
      .catch((error: unknown) => {
        if (uploadGenerationRef.current !== generation) {
          return
        }
        clearParsedFile()
        setUploadFileError(skuFileErrorMessage(error, t))
      })
      .finally(() => {
        if (uploadGenerationRef.current === generation) {
          setSkuFilePhase(null)
        }
      })
  }

  const pastedSkus = useMemo(() => pastedShipmentSkus(pasteText), [pasteText])

  const selectSkuEntry = (value: string) => {
    if (value !== 'upload' && value !== 'paste') {
      return
    }
    if (value === skuEntry) {
      return
    }
    uploadGenerationRef.current += 1
    setReviewItems(null)
    setCreateStep('details')
    setColumnPrompt(null)
    setPendingFileKey(null)
    setSkuFilePhase(null)
    setFormErrors([])
    setUploadFileError(null)
    if (value === 'paste') {
      setShowDropZone(true)
      setSelectedFileName(null)
    } else {
      setPasteText('')
    }
    setSkuEntry(value)
  }

  const detailsErrors = (): string[] => {
    const errors: string[] = []
    const trimmedName = name.trim()
    if (trimmedName.length === 0) {
      errors.push(t('shipmentsPage.skuReview.enterName'))
    } else if (trimmedName.length > 200) {
      errors.push(t('shipmentsPage.skuReview.nameTooLong'))
    }
    if (note.length > 2000) {
      errors.push(t('shipmentsPage.skuReview.noteTooLong'))
    }
    return errors
  }

  const skusForReview = skuEntry === 'paste' ? pastedSkus : (extractedSkus ?? [])
  const pasteOverLimit =
    skuEntry === 'paste' && pastedSkus.length > SHIPMENT_PASTE_MAX_UNIQUE_SKUS
  const canReviewSkus =
    skusForReview.length > 0 &&
    !pasteOverLimit &&
    !parseSkuFile.isPending &&
    !reviewSkus.isPending

  const reviewSkusStep = async () => {
    const validationErrors = detailsErrors()
    if (validationErrors.length > 0) {
      setFormErrors(validationErrors)
      return
    }
    if (skusForReview.length === 0 || pasteOverLimit) {
      return
    }
    setFormErrors([])
    if (reviewItems != null) {
      setCreateStep('review')
      return
    }
    try {
      const review = await reviewSkus.mutateAsync({ skus: skusForReview })
      setReviewItems(review.items)
      setCreateStep('review')
    } catch (error) {
      const serverMessage =
        isAxiosError(error) && typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : null
      setFormErrors([
        serverMessage === 'Workbook has more than 10000 unique SKUs'
          ? t('shipmentsPage.skuFile.pasteTooManyUniqueSkus')
          : t('shipmentsPage.skuReview.reviewFailed'),
      ])
    }
  }

  const handleUploadFileSelected = (file: File) => {
    if (!isAllowedShipmentSkuFile(file)) {
      setUploadFileError(t('shipmentsPage.skuFile.unreadable'))
      return
    }
    setSelectedFileName(file.name)
    void parseSelectedFile(file)
  }

  const continueErrorMessage = (serverMessage: string): string => {
    if (serverMessage === 'Enter a name.') {
      return t('shipmentsPage.skuReview.enterName')
    }
    if (serverMessage === 'Name must be 200 characters or fewer.') {
      return t('shipmentsPage.skuReview.nameTooLong')
    }
    if (serverMessage === 'Note must be 2,000 characters or fewer.') {
      return t('shipmentsPage.skuReview.noteTooLong')
    }
    if (serverMessage === 'At least one matched SKU is required.') {
      return t('shipmentsPage.skuReview.matchedRequired')
    }
    return t('shipmentsPage.createFailed')
  }

  const handleContinue = async () => {
    const validationErrors = detailsErrors()
    if (validationErrors.length > 0) {
      setFormErrors(validationErrors)
      return
    }
    if (reviewItems == null || reviewItems.every((item) => !item.matched)) {
      setFormErrors([t('shipmentsPage.skuReview.matchedRequired')])
      return
    }
    setFormErrors([])
    try {
      const created = await createMutation.mutateAsync({
        name,
        note: note.trim() === '' ? null : note,
        skus: reviewItems.map((item) => item.sku),
      })
      setIsCreateOpen(false)
      resetForm()
      navigate(`/packaging/shipments/${created.id}`)
    } catch (error) {
      const serverMessage =
        isAxiosError(error) && typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : null
      const message =
        serverMessage == null
          ? t('shipmentsPage.createFailed')
          : continueErrorMessage(serverMessage)
      setFormErrors([message])
      toast.error(message)
    }
  }

  const showFormError = formErrors.length > 0
  const formErrorAlert = showFormError && (
    <Alert variant='destructive'>
      <AlertDescription>
        {formErrors.map((message) => (
          <p key={message}>{message}</p>
        ))}
      </AlertDescription>
    </Alert>
  )
  const mappingOpen = columnPrompt != null
  const uploadBusy = parseSkuFile.isPending
  const uploadBusyLabel = parseSkuFile.isPending
    ? skuFilePhase === 'uploading'
      ? t('shipmentsPage.skuFile.uploading')
      : t('shipmentsPage.skuFile.reading')
    : undefined
  const showFileStatus = !showDropZone && uploadBusyLabel != null
  const noneMatched =
    reviewItems != null && reviewItems.every((item) => !item.matched)
  const showingReview = createStep === 'review' && reviewItems != null

  const uploadLabels = {
    title: t('shipmentsPage.skuFile.uploadDialogTitle'),
    formatsRequirement: t('shipmentsPage.skuFile.uploadFormatsRequirement'),
    dragDrop: t('shipmentsPage.skuFile.uploadDragDrop'),
    orClick: t('shipmentsPage.skuFile.uploadOrClick'),
    selectFile: t('shipmentsPage.skuFile.uploadSelectFile'),
  }

  return (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed className='flex min-h-0 flex-1 flex-col gap-4 px-6 sm:gap-6'>
        <div className='flex shrink-0 flex-wrap items-end justify-between gap-2'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>
              {t('pages.shipments.title')}
            </h2>
            <p className='text-muted-foreground'>
              {t('pages.shipments.description')}
            </p>
          </div>
          {canWriteOrgData && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <Plus className='size-4' />
              {t('shipmentsPage.createShipment')}
            </Button>
          )}
        </div>

        {showListError && (
          <div className='flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center'>
            <p className='text-muted-foreground'>
              {t('shipmentsPage.listFailed')}
            </p>
            <Button type='button' variant='outline' onClick={() => refetch()}>
              {t('shipmentsPage.retry')}
            </Button>
          </div>
        )}

        {showEmptyState && (
          <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
            <Package className='size-10 text-muted-foreground/50' />
            <div className='space-y-1 text-center'>
              <h3 className='text-lg font-semibold'>
                {t('shipmentsPage.emptyState.title')}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {t('shipmentsPage.emptyState.description')}
              </p>
            </div>
            {canWriteOrgData && (
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className='size-4' />
                {t('shipmentsPage.createShipment')}
              </Button>
            )}
          </div>
        )}

        {!showEmptyState && !showListError && pageInfo != null && (
          <div className='flex min-h-0 flex-1 flex-col gap-4'>
            {showStatsSkeleton && (
              <div
                className='grid shrink-0 gap-2 sm:grid-cols-2 xl:grid-cols-4'
                aria-busy='true'
                aria-label={t('shipmentsPage.loading')}
              >
                {Array.from(
                  { length: SHIPMENT_STAT_CARD_COUNT },
                  (_, index) => (
                    <PackagingSuppliersStatCardSkeleton key={index} />
                  )
                )}
              </div>
            )}

            {showStats && stats != null && <ShipmentStatsRow stats={stats} />}

            {isLoading && items.length === 0 && (
              <div className='shrink-0 py-12 text-center text-muted-foreground'>
                {t('shipmentsPage.loading')}
              </div>
            )}
            {(items.length > 0 || !isLoading) && (
              <ShipmentsTable
                items={items}
                pageInfo={pageInfo}
                isFetching={isFetching}
                search={search}
                navigate={searchNavigate}
                sortBy={sortBy}
                sortDir={sortDir}
              />
            )}
          </div>
        )}
      </Main>

      {canWriteOrgData && (
        <Dialog
          open={isCreateOpen}
          onOpenChange={(open) => {
            setIsCreateOpen(open)
            if (!open) {
              resetForm()
            }
          }}
        >
          <DialogContent
            className={
              showingReview
                ? 'flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden sm:max-w-2xl'
                : 'max-h-[calc(100vh-2rem)] overflow-y-auto'
            }
          >
            <DialogHeader>
              <DialogTitle>
                {showingReview
                  ? t('shipmentsPage.skuFile.reviewTitle')
                  : t('shipmentsPage.createDialogTitle')}
              </DialogTitle>
            </DialogHeader>
            {formErrorAlert}
            {showingReview && reviewItems != null ? (
              <ShipmentSkuMatchReview items={reviewItems} />
            ) : (
            <div className='space-y-4'>
              <div className='space-y-1'>
                <Label htmlFor='shipment-name'>
                  {t('shipmentsPage.form.name')}
                </Label>
                <Input
                  id='shipment-name'
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className='space-y-1'>
                <Label htmlFor='shipment-note'>
                  {t('shipmentsPage.form.note')}
                </Label>
                <Textarea
                  id='shipment-note'
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  className='field-sizing-fixed h-24 resize-none overflow-y-auto'
                />
                <p className='text-sm text-muted-foreground'>
                  {t('shipmentsPage.form.noteHint')}
                </p>
              </div>
              <Tabs value={skuEntry} onValueChange={selectSkuEntry}>
                <TabsList className='grid w-full grid-cols-2'>
                  <TabsTrigger value='upload'>
                    {t('shipmentsPage.skuFile.uploadTab')}
                  </TabsTrigger>
                  <TabsTrigger value='paste'>
                    {t('shipmentsPage.skuFile.pasteTab')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value='upload' className='pt-2'>
                  {showDropZone ? (
                    <BomImportDropZone
                      accept={SHIPMENT_SKU_FILE_ACCEPT}
                      isBusy={uploadBusy}
                      busyLabel={uploadBusyLabel}
                      fileError={uploadFileError}
                      labels={uploadLabels}
                      onFileSelected={handleUploadFileSelected}
                    />
                  ) : (
                    <div className='flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm'>
                      <div className='min-w-0'>
                        <p className='truncate font-medium'>
                          {selectedFileName}
                        </p>
                        {showFileStatus && (
                          <p
                            className='flex items-center gap-2 text-muted-foreground'
                            role='status'
                          >
                            <Loader2
                              className='size-4 animate-spin'
                              aria-hidden
                            />
                            {uploadBusyLabel}
                          </p>
                        )}
                        {extractedSkus != null && (
                          <p className='text-muted-foreground'>
                            {t('shipmentsPage.skuFile.uploadedSkuCount', {
                              count: extractedSkus.length,
                            })}
                          </p>
                        )}
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          disabled={uploadBusy}
                          onClick={() => {
                            setShowDropZone(true)
                            setExtractedSkus(null)
                            setReviewItems(null)
                            setColumnPrompt(null)
                            setUploadFileError(null)
                          }}
                        >
                          {t('shipmentsPage.skuFile.replaceFile')}
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value='paste' className='space-y-2 pt-2'>
                  <Label htmlFor='shipment-paste-skus'>
                    {t('shipmentsPage.skuFile.pasteLabel')}
                  </Label>
                  <Textarea
                    id='shipment-paste-skus'
                    value={pasteText}
                    rows={6}
                    className='field-sizing-fixed max-h-40 min-h-28 overflow-y-auto'
                    onChange={(event) => {
                      setPasteText(event.target.value)
                      setReviewItems(null)
                    }}
                  />
                  {pastedSkus.length > 0 && (
                    <p className='text-sm text-muted-foreground'>
                      {t('shipmentsPage.skuFile.pasteSkuCount', {
                        count: pastedSkus.length,
                      })}
                    </p>
                  )}
                  {pastedSkus.length > SHIPMENT_PASTE_MAX_UNIQUE_SKUS && (
                    <p className='text-sm text-destructive' role='alert'>
                      {t('shipmentsPage.skuFile.pasteTooManyUniqueSkus')}
                    </p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
            )}
            <DialogFooter>
              {showingReview ? (
                <>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setCreateStep('details')}
                  >
                    {t('shipmentsPage.skuReview.back')}
                  </Button>
                  <Button
                    type='button'
                    disabled={noneMatched || createMutation.isPending}
                    onClick={() => {
                      void handleContinue()
                    }}
                  >
                    {t('shipmentsPage.skuReview.createShipment')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setIsCreateOpen(false)
                      resetForm()
                    }}
                  >
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    type='button'
                    disabled={!canReviewSkus}
                    onClick={() => {
                      void reviewSkusStep()
                    }}
                  >
                    {reviewSkus.isPending
                      ? t('shipmentsPage.skuFile.matching')
                      : t('shipmentsPage.skuFile.reviewSkus')}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canWriteOrgData && columnPrompt != null && (
        <ShipmentSkuColumnMappingDialog
          open={mappingOpen}
          columns={columnPrompt.columns}
          sampleRows={columnPrompt.sampleRows}
          isPending={parseSkuFile.isPending}
          onOpenChange={(open) => {
            if (!open) {
              setColumnPrompt(null)
            }
          }}
          onConfirm={parseChosenColumn}
        />
      )}
    </>
  )
}
